import { Component } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { identify } from '@fff-web/fff-analytics';
import { Modal } from '@fff-web/fff-ui-shared';
import withUniversalNavigationContext from '../../hoc/withUniversalNavigationContext';
import { setBoxData } from '../../modules/box';
import { getCampaigns } from '../../modules/campaigns';
import { getUserSubscriptions, unselectProductDetail } from '../../modules/growth';
import { setTestUserData, setUserData } from '../../modules/user';
import boxStates from '../../utils/boxStates';
import { boxExpired } from '../../utils/boxStatus';
import {
  boxSubscriptionCompletionStatus,
  campaignState as CAMPAIGN_STATE,
  getCustomizeOpenStatus,
} from '../../utils/campaignState';
import {
  getBoxData,
  getCustomizeSaleInfo,
  getMenuData,
  getSaleProducts,
  getUserCartData,
  saveRevealState,
} from '../../utils/data';
import mapGrowthSubscriptionsToProduct from '../../utils/mapGrowthSubscriptionsToProduct';
import { isClubMember } from '../../utils/user';
import Header from '../Header/Header';
import AddOnsPanel from '../Panels/AddOnsPanel/AddOnsPanel';
import BoxSubscriptionPanel from '../Panels/BoxSubscriptionPanel/BoxSubscriptionPanel';
import CustomizePanel from '../Panels/CustomizePanel/CustomizePanel';
import ExplorePanel from '../Panels/ExplorePanel/ExplorePanel';
import DesktopProductModal from '../Subscriptions/DetailProduct/DesktopProductModal/DesktopProductModal';
import MobileDetailProduct from '../Subscriptions/DetailProduct/MobileDetailProduct/MobileDetailProduct';
import ExpiredUserEmptyState from '../Subscriptions/ExpiredUserEmptyState/ExpiredUserEmptyState';
import Subscriptions from '../Subscriptions/Subscriptions';
import loadingIcon from '../../fff-circle-loading.png';
import * as styles from './MyBoxPage.module.css';

// TODO: How do we handle this for growth subscriptions?
const getSubtotalInDollars = (subscriptions) => {
  if (!subscriptions || !Array.isArray(subscriptions)) return 0;

  return subscriptions.reduce((total, sub) => {
    const price = sub.price ?? sub.retailPrice ?? 0;
    const qty = sub.quantity || 1;
    return total + price * qty;
  }, 0);
};

const CADENCE_TYPE = {
  shipWithBox: 'SWB',
  everyThreeMonths: 'P3M',
};

const EMPTY_MENU_USER = {
  customizePartialRollout: false,
  isRefillOnly: false,
  shippingCountry: null,
  subscriptionState: null,
  subscriptionType: null,
  tier: null,
};

class Home extends Component {
  state = {
    sysdata: {
      isMobile: this.props.isMobile,
      getBoxData,
      saveRevealState,
      boxState: '',
    },
    boxSubscriptionProducts: {},
    boxSubscriptionSaleInfo: {},
    boxSubscriptionProcessed: false,
    userCartData: [],
    loadError: null,
  };

  fetchBoxSubscriptionProducts = async (userSubscriptions, countryIsoCode) => {
    const boxSubscriptions = userSubscriptions.filter(
      (sub) => sub.type === 'BOX' && sub.active && !sub.expired && sub.customizeSaleId,
    );

    const productsMap = {};
    const saleInfoMap = {};
    await Promise.all(
      boxSubscriptions.map(async (subscription) => {
        try {
          const [productsData, saleInfoData] = await Promise.all([
            getSaleProducts(subscription.customizeSaleId).catch(() => null),
            getCustomizeSaleInfo(subscription.customizeSaleId, countryIsoCode).catch(() => null),
          ]);

          const { products } = productsData ?? {};
          if (products) {
            const choices = (subscription.choices ?? []).map((choice) => choice.productUuid);
            productsMap[subscription.id] = products.filter(
              (product) =>
                choices.includes(product.productUuid) ||
                product.variants?.some((variant) => choices.includes(variant.productUuid)),
            );
          }

          if (saleInfoData) {
            saleInfoMap[subscription.id] = saleInfoData;
          }
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (error) {
          //nothing to do here
        }
      }),
    );

    this.setState({ boxSubscriptionProducts: productsMap, boxSubscriptionSaleInfo: saleInfoMap });
  };

  componentDidMount() {
    this.props.getUserSubscriptions();
    this.props.getCampaigns();
    Promise.all([getBoxData(), getMenuData(), getUserCartData()])
      .then((values) => {
        const [boxDataRes, menuUserData, userCartInfo] = values;
        const resUserData = { ...EMPTY_MENU_USER, ...menuUserData };
        this.props.setUserData(resUserData);
        this.props.setBoxData(boxDataRes);
        this.setState({ userCartData: userCartInfo });
        this.setState({
          sysdata: {
            ...this.state.sysdata,
            boxData: boxDataRes,
            userData: resUserData,
            revealCallback: this.revealCallback,
          },
        });
      })
      .catch((err) => {
        console.error('error getting required data ¯\\_(ツ)_/¯', err);
        this.setState({ loadError: err });
      });
    identify();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isMobile !== this.props.isMobile) {
      this.setState({
        sysdata: {
          ...this.state.sysdata,
          isMobile: this.props.isMobile,
        },
      });
    }

    // Wait for user data before fetching customize-sale-info — the endpoint is
    // country-scoped (e.g. beauty box ships to Canada), so we need the user's
    // shipping country to avoid getting US-only data for non-US users.
    const userDataLoaded = 'shippingCountry' in this.props.userData;
    const userDataWasLoaded = 'shippingCountry' in prevProps.userData;
    const subscriptionsChanged = prevProps.userSubscriptions !== this.props.userSubscriptions;
    const userDataJustLoaded = userDataLoaded && !userDataWasLoaded;

    if (userDataLoaded && (subscriptionsChanged || userDataJustLoaded)) {
      const countryIsoCode = this.props.userData.shippingCountry || 'US';
      this.fetchBoxSubscriptionProducts(this.props.userSubscriptions, countryIsoCode);
    }
  }

  showExplorePanel = (boxData) => {
    const validStates = [
      boxStates.DELIVERED,
      boxStates.PROB_DELIVERED,
      boxStates.SHIPPED,
      boxStates.PAST_SEASON,
    ];
    return boxData.currentBox && validStates.indexOf(boxData.currentBox.boxStatus) > -1;
  };

  showCustomizePanel = (boxData) => {
    const inValidStates = [
      boxStates.DELIVERED,
      boxStates.PROB_DELIVERED,
      boxStates.SHIPPED,
      boxStates.PAST_SEASON,
    ];
    return !boxData.currentBox || inValidStates.indexOf(boxData.currentBox.boxStatus) < 0;
  };

  // Get the Customize status
  getOpenStatus = (boxData) => getCustomizeOpenStatus(boxData);

  //THIS IS USED FOR REVEAL AND FOR REFRESHING THE CUSTOMIZE TIMER WHEN IT REACHES ZERO
  revealCallback = () => {
    getBoxData().then((boxData) => {
      this.setState({
        sysdata: {
          ...this.state.sysdata,
          boxData,
          // boxData: boxSelfMockData, // Mockdata
        },
      });
    });
  };

  render() {
    const {
      selectedProduct,
      unSelectReplenishment,
      universalNavigationContext,
      userSubscriptions,
    } = this.props;
    // Hand an async load failure to the root FFFErrorBoundary, which renders the shared error
    // page. Re-throwing in render is the only way to reach a boundary from a promise rejection.
    // Menu-data gates the expired/club/refill branches below, so rendering My Box without it would
    // show every member a wrong membership state rather than an honest failure.
    if (this.state.loadError) {
      throw this.state.loadError;
    }

    const { sysdata } = this.state;
    const { boxData, isMobile, userData } = sysdata;
    const isExpired = boxExpired(userData);
    const {
      userSubscriptions: { clubMemberSubscription },
    } = universalNavigationContext;
    const { id: clubMemberSubscriptionId } = clubMemberSubscription ?? {};

    const isClubTier = isClubMember(userData);
    const isCustomizeOpen = this.getOpenStatus(boxData) === CAMPAIGN_STATE.OPEN;

    const mappedSubscriptions = mapGrowthSubscriptionsToProduct(userSubscriptions);

    const activeMappedSubscriptions = mappedSubscriptions.filter(
      (subscription) => subscription.isActive,
    );

    const goWithBoxSubscriptions = activeMappedSubscriptions.filter(
      (subscription) => subscription.cadenceType === CADENCE_TYPE.shipWithBox,
    );

    const ninetyDaysSubscriptions = activeMappedSubscriptions.filter(
      (subscription) => subscription.cadenceType === CADENCE_TYPE.everyThreeMonths,
    );

    const closeModal = () => unSelectReplenishment();
    const shouldDisplayModal = selectedProduct && Object.keys(selectedProduct).length > 0;
    const isSubscriptionProduct = selectedProduct && 'cadenceType' in selectedProduct;
    const ninetyDaysSubscriptionsQuantity = ninetyDaysSubscriptions
      ? ninetyDaysSubscriptions?.length
      : 0;

    // Blank page when data is not returned
    if (!boxData || !!boxData.code) {
      return (
        <div className="pos-r h-100-vh">
          <img
            alt="Loading"
            data-testid="loading-image"
            className="pos-a page-loading-image"
            src={loadingIcon}
          />
        </div>
      );
    }

    if (shouldDisplayModal && isMobile) {
      // TODO: Get campaign title from API
      const campaignTitle = 'Winter 2025 Customize';

      return (
        <MobileDetailProduct
          subscription={selectedProduct}
          backAction={unSelectReplenishment}
          campaignTitle={campaignTitle}
          isMobile
          subscriptionsQuantity={ninetyDaysSubscriptionsQuantity}
          isRefillOnly={userData.isRefillOnly}
        />
      );
    }

    const isExploreShown = this.showExplorePanel(boxData) && !userData.isRefillOnly;
    const isCustomizeShown = this.showCustomizePanel(boxData) && !userData.isRefillOnly;
    const isCustomizePartialRollout = userData.customizePartialRollout === true;
    const showSeasonalBoxSubscriptionPanel = isCustomizePartialRollout && isCustomizeShown;
    const goWithBoxPriceTotal = getSubtotalInDollars(goWithBoxSubscriptions);
    const ninetyDaysPriceTotal = getSubtotalInDollars(ninetyDaysSubscriptions);
    const userBoxSubscriptions = userSubscriptions.filter(
      (subscription) =>
        subscription.type === 'BOX' &&
        subscription.active &&
        !subscription.expired &&
        subscription.customizeCompletionStatus !==
          boxSubscriptionCompletionStatus.WAITING_FOR_PAYMENT &&
        (subscription.customizeSaleType !== 'seasonal_customize_box' ||
          showSeasonalBoxSubscriptionPanel),
    );

    const userExistingCart =
      Array.isArray(this.state.userCartData) &&
      this.state.userCartData?.find((cart) => !cart.checkedOut);

    return (
      <div className={styles.container}>
        {shouldDisplayModal && !isMobile && isSubscriptionProduct && (
          <Modal isOpen={!!(!isMobile && selectedProduct)} onRequestClose={closeModal}>
            <DesktopProductModal
              onCloseModal={closeModal}
              subscription={selectedProduct}
              subscriptionsQuantity={ninetyDaysSubscriptionsQuantity}
              isRefillOnly={userData.isRefillOnly}
            />
          </Modal>
        )}
        {(isExpired || isClubTier) && !userData.isRefillOnly ? (
          <ExpiredUserEmptyState
            isClubTier={isClubTier}
            isCustomizeOpen={isCustomizeOpen}
            isCustomizeShown={isCustomizeShown}
            sysdata={sysdata}
            subscriptions={ninetyDaysSubscriptions}
            ninetyDaysPriceTotal={ninetyDaysPriceTotal}
            isShoppingClubUser={!!clubMemberSubscriptionId}
            userBoxSubscriptions={userBoxSubscriptions}
            boxSubscriptionProducts={this.state.boxSubscriptionProducts}
            boxSubscriptionSaleInfo={this.state.boxSubscriptionSaleInfo}
            userExistingCart={userExistingCart}
          />
        ) : (
          <>
            <Header />
            {userBoxSubscriptions.map((userBoxSubscription) => (
              <BoxSubscriptionPanel
                key={userBoxSubscription.id}
                boxSubscription={userBoxSubscription}
                sysdata={sysdata}
                products={this.state.boxSubscriptionProducts[userBoxSubscription.id] || []}
                customizeSaleInfo={this.state.boxSubscriptionSaleInfo[userBoxSubscription.id]}
                userExistingCart={userExistingCart}
              />
            ))}
            {isExploreShown && <ExplorePanel sysdata={sysdata} boxData={sysdata.boxData} />}
            <AddOnsPanel sysdata={sysdata} />
            {isCustomizeShown && !showSeasonalBoxSubscriptionPanel && (
              <CustomizePanel sysdata={sysdata} />
            )}
            <Subscriptions
              isCustomizeOpen={isCustomizeOpen}
              goWithBoxSubscriptions={goWithBoxSubscriptions}
              goWithBoxPriceTotal={goWithBoxPriceTotal}
              ninetyDaysSubscriptions={ninetyDaysSubscriptions}
              ninetyDaysPriceTotal={ninetyDaysPriceTotal}
              sysdata={sysdata}
              isRefillOnly={userData.isRefillOnly}
            />
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userData: state.user,
  isMobile: state.view.isMobile,
  selectedProduct: state.growth.selectedProduct,
  userSubscriptions: state.growth.userSubscriptions,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setBoxData,
      setUserData,
      setTestUserData,
      getUserSubscriptions,
      getUserCartData,
      getCampaigns,
      unSelectReplenishment: unselectProductDetail,
    },
    dispatch,
  );

Home.propTypes = {
  isMobile: bool.isRequired,
  setBoxData: func.isRequired,
  setUserData: func.isRequired,
  userData: shape({
    customizePartialRollout: bool,
    isRefillOnly: bool,
    shippingCountry: string,
    subscriptionState: string,
    subscriptionType: string,
    tier: string,
  }).isRequired,
  getUserSubscriptions: func.isRequired,
  getCampaigns: func.isRequired,
  selectedProduct: shape({}),
  unSelectReplenishment: func,
  universalNavigationContext: shape({}),
  userSubscriptions: arrayOf(shape({})),
};

Home.defaultProps = {
  selectedProduct: {},
  unSelectReplenishment: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(withUniversalNavigationContext(Home));
